import React, { useState } from 'react';
import './Tracking.css';

const Tracking = () => {
  const [lrNumber, setLrNumber] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState('');
  const [expandedStages, setExpandedStages] = useState({});

  // Mock tracking data - in real app, this would come from API
  const mockTrackingData = {
    'LR-23051001': {
      lrNumber: 'LR-23051001',
      origin: 'Mumbai Warehouse',
      destination: 'Delhi Distribution',
      estimatedDelivery: 'May 15, 2023',
      status: 'In Transit',
      statusMessage: 'Your shipment is currently in transit and is expected to be delivered on time.',
      timeline: [
        {
          stage: 'Placed',
          location: 'Mumbai Warehouse',
          description: 'Order has been placed and confirmed',
          dateTime: 'May 10, 2023 - 09:15 AM',
          status: 'completed',
          icon: '📦'
        },
        {
          stage: 'Dispatched',
          location: 'Mumbai Sorting Center',
          description: 'Shipment has left the origin facility',
          dateTime: 'May 11, 2023 - 02:30 PM',
          status: 'completed',
          icon: '🚚'
        },
        {
          stage: 'In Transit',
          location: 'Nagpur Hub',
          description: 'Shipment is in transit to the destination',
          dateTime: 'May 12, 2023 - 10:45 AM',
          status: 'in-progress',
          icon: '⏰',
          intermediateStops: [
            {
              location: 'Pune Hub',
              dateTime: 'May 11, 2023 - 06:00 PM',
              description: 'Shipment arrived at intermediate stop'
            },
            {
              location: 'Aurangabad Hub',
              dateTime: 'May 12, 2023 - 08:00 AM',
              description: 'Shipment arrived at intermediate stop'
            },
            {
              location: 'Nagpur Hub',
              dateTime: 'May 12, 2023 - 10:45 AM',
              description: 'Shipment arrived at intermediate stop'
            }
          ]
        },
        {
          stage: 'Reached',
          location: 'Delhi Distribution',
          description: 'Shipment will be delivered to the recipient',
          dateTime: '',
          status: 'pending',
          icon: '✓'
        }
      ]
    }
  };

  const handleTrack = () => {
    if (!lrNumber.trim()) {
      setError('Please enter an LR number');
      return;
    }

    const upperLrNumber = lrNumber.trim().toUpperCase();
    const data = mockTrackingData[upperLrNumber];

    if (data) {
      setTrackingData(data);
      setError('');
    } else {
      setTrackingData(null);
      setError('LR number not found. Please check and try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTrack();
    }
  };

  const toggleStageExpansion = (index) => {
    setExpandedStages(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Placed':
      case 'Dispatched':
        return '#10B981'; // Green
      case 'In Transit':
        return '#3B82F6'; // Blue
      case 'Reached':
        return '#6B7280'; // Grey
      default:
        return '#6B7280';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'Placed':
      case 'Dispatched':
        return '#10B981';
      case 'In Transit':
        return '#6366F1'; // Purple/Indigo
      default:
        return '#6B7280';
    }
  };

  return (
    <div className="tracking-container">
      <div className="tracking-header">
        <h1 className="tracking-title">Track Your Shipment</h1>
      </div>

      <div className="tracking-search-section">
        <div className="tracking-search-box">
          <div className="search-icon">🔍</div>
          <input
            type="text"
            className="tracking-input"
            placeholder="LR-23051001"
            value={lrNumber}
            onChange={(e) => setLrNumber(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button className="track-now-btn" onClick={handleTrack}>
            Track Now
          </button>
        </div>
        {error && <div className="tracking-error">{error}</div>}
      </div>

      {trackingData && (
        <>
          <div className="tracking-summary-card">
            <div className="summary-row">
              <div className="summary-item">
                <span className="summary-label">LR Number:</span>
                <span className="summary-value">{trackingData.lrNumber}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Origin:</span>
                <span className="summary-value">{trackingData.origin}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Destination:</span>
                <span className="summary-value">{trackingData.destination}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Estimated Delivery:</span>
                <span className="summary-value">{trackingData.estimatedDelivery}</span>
              </div>
            </div>
          </div>

          <div className="status-banner" style={{ backgroundColor: getStatusBgColor(trackingData.status) }}>
            <span className="status-text">{trackingData.status}</span>
            <span className="status-message">{trackingData.statusMessage}</span>
          </div>

          <div className="tracking-details-section">
            <h2 className="tracking-details-title">Tracking Details</h2>
            <div className="timeline-container">
              {trackingData.timeline.map((item, index) => {
                const isCompleted = item.status === 'completed';
                const isInProgress = item.status === 'in-progress';
                const isPending = item.status === 'pending';
                const isLast = index === trackingData.timeline.length - 1;

                const hasIntermediateStops = item.intermediateStops && item.intermediateStops.length > 0;
                const isExpanded = expandedStages[index];

                return (
                  <div key={index} className="timeline-item">
                    <div className="timeline-content">
                      <div className="timeline-icon-wrapper" style={{ 
                        backgroundColor: isCompleted ? '#10B981' : isInProgress ? '#3B82F6' : '#E5E7EB',
                        color: isCompleted || isInProgress ? 'white' : '#9CA3AF'
                      }}>
                        {isCompleted ? (
                          <div className="icon-package">{item.icon}</div>
                        ) : isInProgress ? (
                          <div className="icon-clock">{item.icon}</div>
                        ) : (
                          <div className="icon-checkmark">{item.icon}</div>
                        )}
                      </div>
                      <div className="timeline-details">
                        <div className="timeline-stage-header">
                          <div>
                            <div className="timeline-stage">{item.stage}</div>
                            <div className="timeline-location">{item.location}</div>
                            <div className="timeline-description">{item.description}</div>
                            {item.dateTime && (
                              <div className="timeline-datetime">{item.dateTime}</div>
                            )}
                            {!item.dateTime && isPending && (
                              <div className="timeline-pending">Pending</div>
                            )}
                          </div>
                          {hasIntermediateStops && (
                            <button 
                              className="expand-toggle-btn"
                              onClick={() => toggleStageExpansion(index)}
                              aria-label={isExpanded ? 'Collapse stops' : 'Expand stops'}
                            >
                              <svg 
                                width="20" 
                                height="20" 
                                viewBox="0 0 20 20" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ 
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.3s ease'
                                }}
                              >
                                <path 
                                  d="M5 7.5L10 12.5L15 7.5" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                        {hasIntermediateStops && isExpanded && (
                          <div className="intermediate-stops-container">
                            <div className="intermediate-stops-label">Intermediate Stops:</div>
                            {item.intermediateStops.map((stop, stopIndex) => (
                              <div key={stopIndex} className="intermediate-stop-item">
                                <div className="intermediate-stop-dot" />
                                <div className="intermediate-stop-details">
                                  <div className="intermediate-stop-location">{stop.location}</div>
                                  <div className="intermediate-stop-description">{stop.description}</div>
                                  <div className="intermediate-stop-datetime">{stop.dateTime}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {!isLast && (
                      <div 
                        className="timeline-line" 
                        style={{ 
                          backgroundColor: isCompleted || isInProgress ? '#10B981' : '#E5E7EB',
                          height: hasIntermediateStops && isExpanded ? 'auto' : '60px',
                          minHeight: '60px'
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Tracking;
